import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { InstallationHistory, CommentHistory } from '../definitions/sensor';
import { formatDateOnlyToCDT } from '../utils/helpers';
import theme from '../theme';
import { Box, Typography, Divider, Button } from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CommentsForm from './CommentsForm';
import { useState } from 'react';

const columns = [
  {
    field: 'action',
    headerName: 'Action',
    width: 150,
    flex: 1,
    sortable: false,
    filterable: false,
    resizable: false,
    headerClassName: 'bold-header',
  },
  {
    field: 'actionDate',
    headerName: 'Action Date',
    flex: 1,
    valueGetter: (value) => formatDateOnlyToCDT(value),
    sortable: false,
    filterable: false,
    headerClassName: 'bold-header',
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    sortable: false,
    filterable: false,
    headerClassName: 'bold-header',
  },
  {
    field: 'siteName',
    headerName: 'Location',
    flex: 1,
    sortable: false,
    filterable: false,
    headerClassName: 'bold-header',
  },
  {
    field: 'clientName',
    headerName: 'Customer',
    flex: 1,
    valueGetter: (value: any) => value === 'No Client' ? '' : value,
    sortable: false,
    filterable: false,
    headerClassName: 'bold-header',
  },
  {
    field: 'replacementSensorId',
    headerName: 'Replacement Id',
    flex: 1,
    valueGetter: (value: any) => value || '',
    sortable: false,
    filterable: false,
    headerClassName: 'bold-header',
  },
  {
    field: 'createdBy',
    headerName: 'Created By',
    flex: 1,
    sortable: false,
    filterable: false,
    headerClassName: 'bold-header',
  },
];

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  fontSize: '1.25rem',
  fontWeight: 'bold',
  color: '#333333',
  textAlign: 'center',
}));

const InstallationsWrap = styled('div')({
  display: 'flex',
  flexDirection: 'column-reverse',
  gap: '24px',
});

const CycleBlock = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#fcfcfc',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
}));

const CommentsSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  borderLeft: `4px solid #f27a54`,
  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)',
}));

export default function InstallationHistoryTable({
  installationHistory,
  commentHistory = []
}: {
  installationHistory: InstallationHistory[],
  commentHistory?: CommentHistory[]
}) {
  const [openCycleId, setOpenCycleId] = useState<number | null>(null);

  const handleOpenCommentDialog = (cycleId: number) => setOpenCycleId(cycleId);
  const handleCloseCommentDialog = () => setOpenCycleId(null);

  // Group every history record by its installationId so a full lifecycle
  // (Added to Warehouse -> Removed from Site -> Added to Site -> Removed from Warehouse)
  // shows as a single "Installation #N". Rows inside a group are ordered by actionDate
  // DESCENDING (id as tiebreaker), and groups are ordered by the date of their latest action.
  const byActionDateDesc = (a: InstallationHistory, b: InstallationHistory) => {
    const diff = new Date(b.actionDate).getTime() - new Date(a.actionDate).getTime();
    return diff !== 0 ? diff : b.id - a.id;
  };

  const groupsById = installationHistory.reduce((acc: Record<number, InstallationHistory[]>, curr: InstallationHistory) => {
    const key = curr.installationId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(curr);
    return acc;
  }, {});

  const installations: InstallationHistory[][] = Object.values(groupsById)
    .map(group => [...group].sort(byActionDateDesc))
    .sort((a, b) => a[0].installationId - b[0].installationId);

  return (
    <Box sx={{ mt: 2 }}>
      <InstallationsWrap>
        {installations.map((installation: InstallationHistory[], index: number) => {
          const cycleNumber = index + 1;
          const cycleLogicalIds = installation.map(h => h.installationId);
          const cycleComments = commentHistory.filter(c => c.installationId && cycleLogicalIds.includes(c.installationId));

          return (
            <CycleBlock key={installation[0].id}>
              <Title variant="h6">Installation #{cycleNumber}</Title>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <DataGrid
                  rows={installation}
                  columns={columns}
                  sx={{
                    width: '100%',
                    minWidth: 700,
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}
                  autoHeight
                  getRowId={(row: InstallationHistory) => row.id}
                  disableRowSelectionOnClick
                  hideFooter
                />
              </Box>
              {cycleComments.length > 0 && (
                <CommentsSection>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.text.secondary, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f27a54' }}></span>
                    Comments (for installation #{cycleNumber})
                  </Typography>
                  {cycleComments.map((comment, i) => (
                    <Box key={i} sx={{ mt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: theme.palette.primary.dark }}>{comment.createdBy}</Typography>
                        <Typography variant="caption" color="textSecondary">{formatDateOnlyToCDT(comment.createdOn)}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontStyle: 'italic' }}>"{comment.comment}"</Typography>
                      {i < cycleComments.length - 1 && <Divider sx={{ my: 1.5, opacity: 0.6 }} />}
                    </Box>
                  ))}
                </CommentsSection>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCommentIcon />}
                  onClick={() => handleOpenCommentDialog(installation[0].installationId)}
                  sx={{ borderColor: '#f27a54', color: '#f27a54', '&:hover': { backgroundColor: 'rgba(242, 122, 84, 0.04)', borderColor: '#e06b3e' } }}
                >
                  Add Installation Comment
                </Button>
              </Box>
            </CycleBlock>
          );
        })}
      </InstallationsWrap>

      {openCycleId !== null && (
        <CommentsForm
          open={true}
          handleClose={handleCloseCommentDialog}
          title={`Add Installation Comment`}
          installationId={openCycleId}
        />
      )}
    </Box>
  )
};
